import { useCallback, useEffect, useState } from 'react'

import { getOperatorsWithUsersCount } from '@/service/operatorsService'
import { useOperatorsStore } from '@/stores/useOperatorsStore'
import { FormattedOperator } from '@/types/operator'

import { Autocomplete } from '../Autocomplete'
import type { OperatorAutocompleteProps } from './OperatorAutocomplete.types'

export const operatorSelectTestId = 'form-operator-select-test-id'
export const operatorLabel = 'Operator'

export const OperatorAutocomplete = ({
  onChange,
  defaultValue,
  disabled,
  required,
  disabledInfo,
  overrideLabel,
  error,
  displayOperatorId = false
}: OperatorAutocompleteProps) => {
  const { operators, setOperators } = useOperatorsStore()
  const [loading, setLoading] = useState(true)

  const findOperatorById = useCallback(
    (id: string | null | undefined) => {
      if (!id) return null
      return operators.find((op) => op.id.toString() === id) || null
    },
    [operators]
  )

  const [selectedOperator, setSelectedOperator] = useState<FormattedOperator | null>(
    findOperatorById(defaultValue)
  )

  const fetchOperators = useCallback(async () => {
    try {
      setLoading(true)
      const fetchedOperators = await getOperatorsWithUsersCount()
      setOperators(fetchedOperators)
    } catch (err) {
      console.error('Error fetching operators:', err)
    } finally {
      setLoading(false)
    }
  }, [setOperators])

  useEffect(() => {
    if (!operators.length) {
      fetchOperators()
    } else {
      setLoading(false)
    }
  }, [operators.length, fetchOperators])

  useEffect(() => {
    setSelectedOperator(findOperatorById(defaultValue))
  }, [defaultValue, findOperatorById])

  const handleChange = (value: FormattedOperator | null) => {
    setSelectedOperator(value)
    onChange?.(value ? value.id.toString() : null)
  }

  const formatOption = (option: FormattedOperator) => {
    if (displayOperatorId) {
      return `${option.id} - ${option.name}`
    }

    return option.name
  }

  return (
    <Autocomplete
      label={overrideLabel || operatorLabel}
      required={required}
      disabled={disabled}
      disabledInfo={disabledInfo}
      options={operators}
      defaultValue={selectedOperator}
      onChange={handleChange}
      loading={loading}
      error={error}
      fullWidth
      testId={operatorSelectTestId}
      filterOptions={(options, { inputValue }) =>
        options.filter((option) =>
          formatOption(option).toLowerCase().includes(inputValue.toLowerCase())
        )
      }
      getOptionLabel={formatOption}
    />
  )
}
